const _ = require("lodash");

const {extendIn} = require("../../../../shared/ImmHelpers");

// Convenience functions for manipulating load state objects, which are objects
// that (possibly among others) have keys load and loadError. load is one of
// the strings "none", "pending", "done", "error". loadError is falsy if load
// is not "error" and otherwise contains error information.

// Provide an object. Returns it with load: none, loadError: null. If a non-
// object is provided, returns an object with just load and loadError.
const loadInit = function(obj) {
	return extendIn(obj, [], {
		load: "none",
		loadError: null,
	});
};

// Provide an object that may or may not have been starting to load.
// Returns it with load: none, loadError: null. If a non-
// object is provided, returns an object with just load and loadError.
// Used to differ from loadInit, but not anymore. TODO: remove?
const loadRestart = function(obj) {
	return extendIn(obj, [], {
		load: "none",
		loadError: null,
	});
};

// Provide a load state object, plus a function to call that would start the
// loading process. If load is none or error, call the load function and return
// a modified object with load: pending.
const loadStart = function(obj, loadFunc) {
	if (obj.load === "none" || obj.load === "error") {
		loadFunc();
		return extendIn(obj, [], {
			load: "pending",
			loadError: null,
		});
	}
	return obj;
};

// Provide a load state object. Returns it with load: pending. Use instead of
// loadStart if you are managing the starting of loads yourself.
const loadPending = function(obj) {
	return extendIn(obj, [], {
		load: "pending",
		loadError: null,
	});
};

// Provide a load state object. Returns it with load: done.
const loadSuccess = function(obj) {
	return extendIn(obj, [], {
		load: "done",
		loadError: null,
	});
};

// Provide a load state object. Returns it with load: error and loadError set.
const loadError = function(obj, error) {
	return extendIn(obj, [], {
		load: "error",
		loadError: error,
	});
};

// Pass an error message. Returns whether to retry the endpoint immediately if
// it loads data and it failed with this error.
const shouldRetryImmediately = function(error) {
	return error === "E_NOT_CONNECTED" || /E_SERVER_SHUTDOWN/.test(error);
};

// Pass an error message. Returns whether to retry the endpoint eventually if
// it loads data and it failed with this error. Some errors are here but not
// shouldRetryImmediately so that a long data loading request doesn't stack up
// on our server. (T47245)
const shouldRetry = function(error) {
	return (
		error === "E_NOT_CONNECTED" ||
		/E_SERVER_SHUTDOWN/.test(error) ||
		error === "E_REQUEST_TIMEOUT" ||
		error === "E_REQUEST_TIMEOUT_WITH_PING"
	);
};

// Pass two values of load. Returns a value for load that gives the status of
// a load waiting on both of them.
const reduceStatus = function(load1, load2) {
	if (load1 === "error" || load2 === "error") {
		return "error";
	} else if (load1 === "pending" || load2 === "pending") {
		return "pending";
	} else if (load1 === "none" || load2 === "none") {
		return "none";
	} else {
		return "done";
	}
};

const reduceToSingleLoad = loads =>
	loads.reduce(
		(status, chapter) => reduceStatus(status, chapter.load),
		loadSuccess()
	);

/**
 * Takes a collection of objects with load statuses and returns just
 * the ones that are done loading.
 * @param {Object|Array} collection
 * @param {String} status "none", "pending", "done", or "error"
 * @returns {Array}
 */
const filterByStatus = (collection, status) => {
	if (_.isEmpty(collection)) return [];
	if (_.isObject(collection)) collection = Object.values(collection);

	return collection.filter(obj => _.get(obj, "load") === status);
};

module.exports = {
	loadInit,
	loadRestart,
	loadStart,
	loadPending,
	loadSuccess,
	loadError,
	shouldRetryImmediately,
	shouldRetry,
	reduceStatus,
	reduceToSingleLoad,
	filterByStatus,
};
